import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { collectionsApi } from '../api';
import { transformApiCollection, transformApiCollections } from '../utils/data-transforms';
import { useSession } from '@/lib/auth-client';
import type {
  ApiCollection,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  Collection,
  CollectionsQuery,
  CollectionsResponse,
} from '../api/types';
import * as _ from 'es-toolkit';

// Query keys
export const queryKeys = {
  collections: (query?: CollectionsQuery) => ['collections', query] as const,
  collection: (id: number) => ['collections', id] as const,
} as const;

// Hook to fetch user's collections with infinite loading support
export function useCollections(
  query?: CollectionsQuery,
  options?: Partial<UseInfiniteQueryOptions>
) {
  const { data: session, isPending: isSessionPending } = useSession();

  return useInfiniteQuery({
    queryKey: queryKeys.collections(query),
    queryFn: async (x) => {
      // Only include nextCursor if it's not empty
      const queryWithCursor = x.pageParam
        ? _.merge({ nextCursor: x.pageParam }, query || {})
        : query;

      const response: CollectionsResponse = await collectionsApi.getCollections(queryWithCursor);
      return {
        ...response,
        collections: transformApiCollections(response.collections),
      };
    },
    staleTime: 1000 * 10,
    getNextPageParam: (lastPage) => lastPage.pagination.nextCursor,
    initialPageParam: '',
    // @ts-ignore
    ...(options as any),
  });
}

// Hook to fetch a single collection with its items
export function useCollection(
  id: number,
  options?: {
    itemLimit?: number;
    includeEngagement?: boolean;
    enabled?: boolean;
  }
) {
  const { data: session, isPending: isSessionPending } = useSession();

  return useQuery({
    queryKey: [...queryKeys.collection(id), options?.itemLimit, options?.includeEngagement],
    queryFn: async (): Promise<Collection> => {
      try {
        const apiCollection: ApiCollection = await collectionsApi.getCollection(id, {
          itemLimit: options?.itemLimit,
          includeEngagement: options?.includeEngagement,
        });
        const transformed = transformApiCollection(apiCollection);
        return transformed;
      } catch (error) {
        console.error('Error in useCollection queryFn:', error);
        throw error;
      }
    },
    // Temporarily disable auth check for testing since API is hardcoded to user 31
    enabled: options?.enabled !== undefined ? options.enabled : !!id, // Allow explicit control via options
    // enabled: !!id && !!session?.user && !isSessionPending, // Only run when authenticated and ID is provided
    staleTime: 1000 * 10,
    retry: 1, // Reduce retries for debugging
  });
}

// Hook to create a new collection
export function useCreateCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCollectionRequest) => {
      const newCollection: ApiCollection = await collectionsApi.createCollection(data);
      return transformApiCollection(newCollection);
    },
    onMutate: async (data) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: queryKeys.collections() });

      // Snapshot the previous values for both user and public collections queries
      const userCollectionsQueryKey = queryKeys.collections({ userOnly: true });
      const publicCollectionsQueryKey = queryKeys.collections({ includeProfiled: true });
      const previousUserCollections = queryClient.getQueryData(userCollectionsQueryKey);
      const previousPublicCollections = queryClient.getQueryData(publicCollectionsQueryKey);

      // Create optimistic collection with temporary ID
      const optimisticCollection = {
        id: Date.now(), // Temporary ID that will be replaced by server response
        name: data.name,
        description: data.description || null,
        isPublic: data.isPublic,
        items: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update user collections (always add to user's collections)
      queryClient.setQueryData(userCollectionsQueryKey, (old: any) => {
        if (!old) return [optimisticCollection];
        return [optimisticCollection, ...old];
      });

      // If the collection is public, also add it to the public collections cache
      if (data.isPublic) {
        queryClient.setQueryData(publicCollectionsQueryKey, (old: any) => {
          if (!old) return [optimisticCollection];
          return [optimisticCollection, ...old];
        });
      }

      // Return a context object with the snapshotted values
      return {
        previousUserCollections,
        previousPublicCollections,
        userCollectionsQueryKey,
        publicCollectionsQueryKey,
        optimisticCollection,
      };
    },
    onSuccess: (newCollection, variables, context) => {
      // Replace the optimistic collection with the real one from the server
      const userCollectionsQueryKey = queryKeys.collections({ userOnly: true });
      const publicCollectionsQueryKey = queryKeys.collections({ includeProfiled: true });

      // Update user collections cache
      queryClient.setQueryData(userCollectionsQueryKey, (old: any) => {
        if (!old) return [newCollection];

        // Replace the optimistic collection (with temporary ID) with the real one
        return old.map((collection: any) =>
          collection.id == context?.optimisticCollection.id ? newCollection : collection
        );
      });

      // If the collection is public, also update the public collections cache
      if (newCollection.isPublic) {
        queryClient.setQueryData(publicCollectionsQueryKey, (old: any) => {
          if (!old) return [newCollection];

          // Replace the optimistic collection (with temporary ID) with the real one
          return old.map((collection: any) =>
            collection.id == context?.optimisticCollection.id ? newCollection : collection
          );
        });
      }
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousUserCollections && context?.userCollectionsQueryKey) {
        queryClient.setQueryData(context.userCollectionsQueryKey, context.previousUserCollections);
      }
      if (context?.previousPublicCollections && context?.publicCollectionsQueryKey) {
        queryClient.setQueryData(
          context.publicCollectionsQueryKey,
          context.previousPublicCollections
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: queryKeys.collections() });
    },
  });
}

// Hook to update an existing collection
export function useUpdateCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateCollectionRequest }) => {
      const updatedCollection: ApiCollection = await collectionsApi.updateCollection(id, data);
      return transformApiCollection(updatedCollection);
    },
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: queryKeys.collections() });

      // Snapshot the previous value for the user collections query
      const userCollectionsQueryKey = queryKeys.collections({ userOnly: true });
      const previousCollections = queryClient.getQueryData(userCollectionsQueryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(userCollectionsQueryKey, (old: any) => {
        if (!old) return old;

        return old.map((collection: any) => {
          if (collection.id == id) {
            return {
              ...collection,
              name: data.name || collection.name,
              description:
                data.description !== undefined ? data.description : collection.description,
              isPublic: data.isPublic !== undefined ? data.isPublic : collection.isPublic,
            };
          }
          return collection;
        });
      });

      // Return a context object with the snapshotted value
      return { previousCollections, userCollectionsQueryKey };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCollections && context?.userCollectionsQueryKey) {
        queryClient.setQueryData(context.userCollectionsQueryKey, context.previousCollections);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: queryKeys.collections() });
    },
  });
}

// Hook to delete a collection
export function useDeleteCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await collectionsApi.deleteCollection(id);
      return id;
    },
    onMutate: async (id) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: queryKeys.collections() });

      // Snapshot the previous value for the user collections query
      const userCollectionsQueryKey = queryKeys.collections({ userOnly: true });
      const previousCollections = queryClient.getQueryData(userCollectionsQueryKey);

      // Optimistically remove the collection from the cache
      queryClient.setQueryData(userCollectionsQueryKey, (old: any) => {
        if (!old) return old;
        return old.filter((collection: any) => collection.id !== id);
      });

      // Return a context object with the snapshotted value
      return { previousCollections, userCollectionsQueryKey };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCollections && context?.userCollectionsQueryKey) {
        queryClient.setQueryData(context.userCollectionsQueryKey, context.previousCollections);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: queryKeys.collections() });
    },
  });
}

// Hook to add an item to a collection
export function useAddItemToCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ collectionId, itemId }: { collectionId: number; itemId: string }) => {
      const response = await collectionsApi.addItemToCollection(collectionId, itemId);
      return { collectionId, itemId, userCollections: response.userCollections };
    },
    onSuccess: (data, { itemId, collectionId }) => {
      // Convert itemId to number for comparison (API items have numeric IDs)
      const itemIdNum = Number(itemId);

      // Update the single item query cache
      queryClient.setQueryData(['items', itemId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          userCollections: data.userCollections,
        };
      });

      // Update all paginated items queries
      queryClient.setQueriesData({ queryKey: ['items'] }, (old: any) => {
        if (!old || !old.pages) return old;

        // Check if any page has the item
        const hasItem = old.pages.some((page: any) =>
          page.items?.some((item: any) => item.id == itemIdNum)
        );

        if (!hasItem) return old;

        // Create new structure with updated item - ensure all levels are new objects
        const newPages = old.pages.map((page: any) => {
          const hasMatchingItem = page.items?.some((item: any) => item.id == itemIdNum);
          if (!hasMatchingItem) return page;

          return {
            ...page,
            items: page.items.map((item: any) => {
              if (item.id == itemIdNum) {
                return {
                  ...item,
                  userCollections: data.userCollections,
                };
              }
              return item;
            }),
          };
        });

        return {
          ...old,
          pages: newPages,
          pageParams: old.pageParams,
        };
      });

      // Notify all subscribers that items queries have been updated
      queryClient.invalidateQueries({
        queryKey: ['items'],
        refetchType: 'none', // Don't refetch, just notify subscribers
      });

      // Update quiz submissions cache - items are indexed by item_<id>
      queryClient.setQueriesData({ queryKey: ['quizzes'] }, (old: any) => {
        if (!old || !old.pages) return old;

        // Check if any page has items object with our item
        const itemKey = `item_${itemId}`;
        const hasItem = old.pages.some((page: any) => page.items && page.items[itemKey]);

        if (!hasItem) return old;

        // Create new structure with updated item
        const newPages = old.pages.map((page: any) => {
          if (!page.items || !page.items[itemKey]) return page;

          return {
            ...page,
            items: {
              ...page.items,
              [itemKey]: {
                ...page.items[itemKey],
                userCollections: data.userCollections,
              },
            },
          };
        });

        return {
          ...old,
          pages: newPages,
          pageParams: old.pageParams,
        };
      });

      // Update the specific collection query to add the item to its items array
      queryClient.setQueriesData(
        { queryKey: ['collections', collectionId], exact: false },
        (old: any) => {
          if (!old || !old.id) return old;

          // Check if item is already in the collection
          const itemExists = old.items?.some((item: any) => item.id == itemIdNum);
          if (itemExists) {
            // Item already exists, just update its userCollections
            return {
              ...old,
              items: old.items?.map((item: any) => {
                if (item.id == itemIdNum) {
                  return {
                    ...item,
                    userCollections: data.userCollections,
                  };
                }
                return item;
              }),
            };
          }

          // Item doesn't exist in collection, try to find it in cache and add it
          // First check single item cache
          let itemData: any = queryClient.getQueryData(['items', itemId]);

          // If not found, search in paginated items cache
          if (!itemData) {
            const paginatedQueries = queryClient.getQueriesData({ queryKey: ['items'] });
            for (const [, queryData] of paginatedQueries) {
              if (queryData && typeof queryData === 'object' && 'pages' in queryData) {
                const pages = (queryData as any).pages;
                for (const page of pages) {
                  const foundItem = page.items?.find((item: any) => item.id == itemIdNum);
                  if (foundItem) {
                    itemData = foundItem;
                    break;
                  }
                }
                if (itemData) break;
              }
            }
          }

          if (itemData && typeof itemData === 'object') {
            return {
              ...old,
              items: [
                ...(old.items || []),
                {
                  ...itemData,
                  userCollections: data.userCollections,
                },
              ],
            };
          }

          return old;
        }
      );

      // Update the collection cache with the server response
      queryClient.setQueriesData({ queryKey: ['collections'] }, (old: any) => {
        if (!old) return old;

        // Handle single collection query
        if (old.id) {
          return {
            ...old,
            items: old.items?.map((item: any) => {
              if (item.id == itemIdNum) {
                return {
                  ...item,
                  userCollections: data.userCollections,
                };
              }
              return item;
            }),
          };
        }

        return old;
      });

      // Update quiz question analytics cache - items are in analytics[].items object
      queryClient.setQueriesData({ queryKey: ['quiz-question-analytics'] }, (old: any) => {
        if (!old || !old.analytics) return old;

        const itemKey = `item_${itemId}`;

        // Check if any analytics entry has our item
        const hasItem = old.analytics.some((entry: any) => entry.items && entry.items[itemKey]);

        if (!hasItem) return old;

        // Update the item in analytics entries
        return {
          ...old,
          analytics: old.analytics.map((entry: any) => {
            if (!entry.items || !entry.items[itemKey]) return entry;

            return {
              ...entry,
              items: {
                ...entry.items,
                [itemKey]: {
                  ...entry.items[itemKey],
                  userCollections: data.userCollections,
                },
              },
            };
          }),
        };
      });

      // Update search results cache
      queryClient.setQueriesData({ queryKey: ['search'] }, (old: any) => {
        if (!old || !old.results) return old;

        return {
          ...old,
          results: old.results.map((result: any) => {
            // Check if this result matches the item we're updating
            if (String(result.objectId) !== itemId || result.objectType !== 'item') {
              return result;
            }

            // Update userCollections
            return {
              ...result,
              userCollections: data.userCollections,
            };
          }),
        };
      });
    },
  });
}

// Hook to remove an item from a collection
export function useRemoveItemFromCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ collectionId, itemId }: { collectionId: number; itemId: string }) => {
      const response = await collectionsApi.removeItemFromCollection(collectionId, itemId);
      return { collectionId, itemId, userCollections: response.userCollections };
    },
    onSuccess: (data, { itemId, collectionId }) => {
      // Convert itemId to number for comparison (API items have numeric IDs)
      const itemIdNum = Number(itemId);

      // Update the single item query cache
      queryClient.setQueryData(['items', itemId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          userCollections: data.userCollections,
        };
      });

      // Update all paginated items queries
      queryClient.setQueriesData({ queryKey: ['items'] }, (old: any) => {
        if (!old || !old.pages) return old;

        // Check if any page has the item
        const hasItem = old.pages.some((page: any) =>
          page.items?.some((item: any) => item.id == itemIdNum)
        );

        if (!hasItem) return old;

        // Create new structure with updated item - ensure all levels are new objects
        const newPages = old.pages.map((page: any) => {
          const hasMatchingItem = page.items?.some((item: any) => item.id == itemIdNum);
          if (!hasMatchingItem) return page;

          return {
            ...page,
            items: page.items.map((item: any) => {
              if (item.id == itemIdNum) {
                return {
                  ...item,
                  userCollections: data.userCollections,
                };
              }
              return item;
            }),
          };
        });

        return {
          ...old,
          pages: newPages,
          pageParams: old.pageParams,
        };
      });

      // Notify all subscribers that items queries have been updated
      queryClient.invalidateQueries({
        queryKey: ['items'],
        refetchType: 'none', // Don't refetch, just notify subscribers
      });

      // Update quiz submissions cache - items are indexed by item_<id>
      queryClient.setQueriesData({ queryKey: ['quizzes'] }, (old: any) => {
        if (!old || !old.pages) return old;

        // Check if any page has items object with our item
        const itemKey = `item_${itemId}`;
        const hasItem = old.pages.some((page: any) => page.items && page.items[itemKey]);

        if (!hasItem) return old;

        // Create new structure with updated item
        const newPages = old.pages.map((page: any) => {
          if (!page.items || !page.items[itemKey]) return page;

          return {
            ...page,
            items: {
              ...page.items,
              [itemKey]: {
                ...page.items[itemKey],
                userCollections: data.userCollections,
              },
            },
          };
        });

        return {
          ...old,
          pages: newPages,
          pageParams: old.pageParams,
        };
      });

      // Update the specific collection query to remove the item from its items array
      // Use setQueriesData with partial match to handle all variants of the collection query key
      queryClient.setQueriesData(
        { queryKey: ['collections', collectionId], exact: false },
        (old: any) => {
          if (!old || !old.id) return old;
          return {
            ...old,
            items: old.items?.filter((item: any) => item.id != itemIdNum) || [],
          };
        }
      );

      // Update the collection cache with the server response
      queryClient.setQueriesData({ queryKey: ['collections'] }, (old: any) => {
        if (!old) return old;

        // Handle single collection query
        if (old.id) {
          return {
            ...old,
            items: old.items?.map((item: any) => {
              if (item.id == itemIdNum) {
                return {
                  ...item,
                  userCollections: data.userCollections,
                };
              }
              return item;
            }),
          };
        }

        return old;
      });

      // Update quiz question analytics cache - items are in analytics[].items object
      queryClient.setQueriesData({ queryKey: ['quiz-question-analytics'] }, (old: any) => {
        if (!old || !old.analytics) return old;

        const itemKey = `item_${itemId}`;

        // Check if any analytics entry has our item
        const hasItem = old.analytics.some((entry: any) => entry.items && entry.items[itemKey]);

        if (!hasItem) return old;

        // Update the item in analytics entries
        return {
          ...old,
          analytics: old.analytics.map((entry: any) => {
            if (!entry.items || !entry.items[itemKey]) return entry;

            return {
              ...entry,
              items: {
                ...entry.items,
                [itemKey]: {
                  ...entry.items[itemKey],
                  userCollections: data.userCollections,
                },
              },
            };
          }),
        };
      });

      // Update search results cache
      queryClient.setQueriesData({ queryKey: ['search'] }, (old: any) => {
        if (!old || !old.results) return old;

        return {
          ...old,
          results: old.results.map((result: any) => {
            // Check if this result matches the item we're updating
            if (String(result.objectId) !== itemId || result.objectType !== 'item') {
              return result;
            }

            // Update userCollections
            return {
              ...result,
              userCollections: data.userCollections,
            };
          }),
        };
      });
    },
  });
}
